import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import { Icon } from "@material-ui/core";
import { ActionPermissionsObject, IWorkspaceResourcePresentationTemplate, WorkspaceResourceType, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import Banner from "js/Components/Banner";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import { sortTagsByPopularity } from "js/core/utilities/utilities";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import Loadable from "js/react/components/Loadable";
import { NoMatchNotice, NoTeamTemplateNotice } from "js/react/components/Notice";
import { FlexBox } from "js/react/components/LayoutGrid";
import { BlueButton, UIPane } from "js/react/components/UiComponents";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import {
    UIPaneResultsContainer,
    UIPaneResultsInnerContainer
} from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { TeamResourcesHeaderFilter, TeamResourcesPaneHeader } from "js/react/views/TeamResources/components/TeamResourcesPaneHeader";

import { PresentationTemplateItem } from "./PresentationTemplateItem";
import {
    PresentationTemplateListProps,
    PresentationTemplatesPaneContentsProps,
    PresentationTemplatesPaneProps
} from "./types";
import { t } from "js/i18n";

const CONTENT_FILTER_ALL = "all";
const TAG_FILTER_ALL = "  all  "; // Spaces needed to prevent conflict with other tags

const BASE_CONTENT_FILTER_OPTIONS = [
    {
        id: "name",
        nameKey: "Name",
        isMatch: (item, searchTerm) => item.metadata.name?.toLowerCase().includes(searchTerm)
    },
    {
        id: "description",
        nameKey: "Description",
        isMatch: (item, searchTerm) => item.metadata.description?.toLowerCase().includes(searchTerm)
    },
    {
        id: "tags",
        nameKey: "Tags",
        isMatch: (item, searchTerm) => item.tags?.some(tag => tag.toLowerCase().includes(searchTerm))
    }
];

const CONTENT_FILTER_OPTIONS = [
    {
        id: CONTENT_FILTER_ALL,
        nameKey: "All Content",
        isMatch: (item, searchTerm) => BASE_CONTENT_FILTER_OPTIONS.some(option => option.isMatch(item, searchTerm))
    },
    ...BASE_CONTENT_FILTER_OPTIONS
];

const SORT_OPTIONS = [
    {
        value: "name",
        labelKey: "Name",
        icon: <Icon>sort_by_alpha</Icon>,
        sortFn: (a, b) => a.metadata.name < b.metadata.name ? -1 : 1
    },
    {
        value: "ratedByCount",
        labelKey: "Rating",
        reverse: true,
        icon: <Icon>star</Icon>,
        sortFn: (a, b) => a.ratedByCount < b.ratedByCount ? 1 : -1
    },
    {
        value: "createdAt",
        labelKey: "Recently Created",
        reverse: true,
        icon: <Icon>date_range</Icon>,
        sortFn: (a, b) => a.createdAt < b.createdAt ? 1 : -1

    },
    {
        value: "modifiedAt",
        labelKey: "Recently Modified",
        reverse: true,
        icon: <Icon>update</Icon>,
        sortFn: (a, b) => a.modifiedAt < b.modifiedAt ? 1 : -1
    },
];

const StyledUIPane = styled(UIPane as any)`
    padding: 24px 20px 0;    
    display: flex;
    flex-direction: column;
`;

const StyledResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0 0 20px;
`;

const StyledResultsInnerContainer = styled(UIPaneResultsInnerContainer)`
    position: static;
    left: 0;
    right: 0;
`;

const PresentationTemplateList = function(props: PresentationTemplateListProps) {
    let {
        editable,
        presentationTemplateResources,
        itemSelected,
        onSelection,
        onConfirm,
        existingTags,
        showBanner
    } = props;

    const hasNoTeamTemplates = presentationTemplateResources.length === 0;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const hasNoMatch = presentationTemplateResources.length > 0 &&
        presentationTemplateResources.filter(item => item.visible).length === 0;

    return (
        <StyledResultsContainer style={showBanner ? { paddingTop: "20px" } : {}}>
            <StyledResultsInnerContainer>
                {hasNoMatch && <NoMatchNotice/>}
                {
                    hasNoTeamTemplates
                        ? <NoTeamTemplateNotice/>
                        : presentationTemplateResources.map(templateResource =>
                            (<PresentationTemplateItem
                                key={templateResource.id}
                                templateResource={templateResource}
                                editable={editable}
                                existingTags={existingTags}
                                selected={templateResource.id === itemSelected?.id}
                                onConfirm={onConfirm}
                                onSelection={onSelection}
                                visible={templateResource.visible}
                                canUpgradeToManageSharedResources={canUpgradeToManageSharedResources}
                            />))

                }
            </StyledResultsInnerContainer>
        </StyledResultsContainer>);
};

const PresentationTemplatesPaneContents = function(props: PresentationTemplatesPaneContentsProps) {
    const {
        editable,
        onConfirm,
        onSelection,
        presentationTemplateResources = []
    } = props;
    const [contentFilter, setContentFilter] = useState(CONTENT_FILTER_ALL);
    const [tagFilter, setTagFilter] = useState(TAG_FILTER_ALL);
    const [itemSelected, setItemSelected] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("modifiedAt");
    const contentFilterOptions = useMemo(() => CONTENT_FILTER_OPTIONS.map(option => ({
        ...option,
        name: t(option.nameKey)
    })), [t]);
    const sortOptions = useMemo(() => SORT_OPTIONS.map(option => ({
        ...option,
        label: t(option.labelKey)
    })), [t]);

    const isEditableOrPublished = useCallback(
        templateResource => editable || templateResource?.metadata?.isPublished,
        [editable]
    );

    const sortedResourcePresentationTemplates = useMemo(() => {
        const isTagMatch = (templateResource: IWorkspaceResourcePresentationTemplate) =>
            tagFilter === TAG_FILTER_ALL ||
                (templateResource.tags || []).includes(tagFilter);

        const isSearchMatch = (templateResource: IWorkspaceResourcePresentationTemplate) =>
            !searchTerm?.length ||
                contentFilterOptions.find(x => x.id === contentFilter)?.isMatch(templateResource, searchTerm);

        return presentationTemplateResources
            .filter(isEditableOrPublished)
            .map(templateResource => ({
                ...templateResource,
                visible: isTagMatch(templateResource) && isSearchMatch(templateResource)
            }))
            .sort(sortOptions
                .find(option => option.value === sortBy)
                .sortFn);
    }, [editable,
        contentFilter,
        presentationTemplateResources,
        searchTerm,
        sortBy,
        tagFilter]
    );

    const getSortedTags = useCallback(() => {
        return sortTagsByPopularity(
            presentationTemplateResources
                .filter(isEditableOrPublished)
                .map(item => item.tags)
        );
    },
    [presentationTemplateResources, editable]
    );

    const handleSearch = async searchTerm => {
        setSearchTerm(searchTerm?.trim().toLowerCase());
    };

    const handleChangeSelection = item => {
        setItemSelected(item);
        onSelection && onSelection(item);
    };

    const handleAddItem = () => {
        AppController.showCreatePresentationDialog({
            pane: CreatePresentationDialogPaneType.BLANK_PRESENTATION,
            isTeamTemplate: true
        });
    };

    const existingTags = getSortedTags();

    let tagOptions = [
        {
            id: TAG_FILTER_ALL, // Spaces needed to prevent conflict with other tags
            name: t("All Tags"),
        },
        ...existingTags.map(tag => {
            return {
                id: tag,
                name: tag,
            };
        }),
    ];

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    return (
        <StyledUIPane>
            {canManageSharedResources && (
                <TeamResourcesPaneHeader
                    title={t("Presentation Templates")}
                    action={editable ? (
                        <BlueButton onClick={handleAddItem}>
                            {t("Create Presentation Template")}
                        </BlueButton>
                    ) : null}
                    onSearch={handleSearch}
                    searchPlaceholder={t("Search team templates")}
                    filterContent={(
                        <>
                            <TeamResourcesHeaderFilter
                                value={tagFilter}
                                onChange={event => setTagFilter(event.target.value)}
                                options={tagOptions}
                            />
                            <TeamResourcesHeaderFilter
                                value={contentFilter}
                                onChange={event => setContentFilter(event.target.value)}
                                options={contentFilterOptions}
                            />
                        </>
                    )}
                    showControls
                    showViewToggle
                    sort={sortOptions.find(option => option.value === sortBy)}
                    sortOptions={sortOptions}
                    onSetSort={({ field }) => {
                        setSortBy(field);
                    }}
                />
            )}
            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth padding="20px 0px 20px 0px">
                    <Banner
                        headerText={t("Unlock Team Presentation Templates")}
                        bodyText={t("Upgrade to a Team plan to give your team a head start with a library of presentation templates.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_templates" }
                            })}
                    />
                </FlexBox>
            )}
            <PresentationTemplateList
                editable={editable}
                presentationTemplateResources={sortedResourcePresentationTemplates}
                itemSelected={itemSelected}
                onSelection={handleChangeSelection}
                onConfirm={onConfirm}
                existingTags={existingTags}
                showBanner={canUpgradeToManageSharedResources}
            />
        </StyledUIPane>
    );
};

export const PresentationTemplatesPane = WorkspaceController.withInitializedState(
    function PresentationTemplatesPane(props: PresentationTemplatesPaneProps & WorkspaceControllerState) {
        const {
            editable,
            initialized,
            resources,
            onConfirm,
            onSelection,
            onReady
        } = props;

        const presentationTemplateResources = useMemo(() =>
                resources.filter(p => p.resourceType === WorkspaceResourceType.PRESENTATION_TEMPLATE) as IWorkspaceResourcePresentationTemplate[],
        [resources]);

        return (
            <Loadable isLoading={!initialized}>
                {onReady && typeof onReady === "function" &&
                    <OnMountedReporter onMounted={onReady}/>
                }
                <PresentationTemplatesPaneContents
                    editable={editable}
                    presentationTemplateResources={presentationTemplateResources}
                    onConfirm={onConfirm}
                    onSelection={onSelection}
                />
            </Loadable>
        );
    }
);
